import React, { useEffect, useRef, useState } from "react";
import { X, Plus, ChevronDown, Clock, Minus } from "lucide-react";
import Qus_swap from "../../Images/swap_qus.svg";
import { API_URL } from "../../config/api";
import { uploadImageToCloudinary } from "../../config/cloudinary";
import ToastMessage from "../../config/ToastMessage";

/**
 * Parses time strings like "30min", "45mins", "1hour", "2hours" into seconds.
 * Returns null if invalid.
 */
const parseTimeToSeconds = (timeStr) => {
  if (!timeStr || typeof timeStr !== "string") return null;
  const trimmed = timeStr.trim().toLowerCase();
  const match = trimmed.match(/^(\d+)\s*(min|mins|hour|hours)$/);
  if (!match) return null;
  const value = parseInt(match[1], 10);
  const unit = match[2];
  if (unit === "min" || unit === "mins") {
    return value * 60;
  } else if (unit === "hour" || unit === "hours") {
    return value * 3600;
  }
  return null;
};

/**
 * Converts seconds back to time string format like "30min" or "1hour".
 * Returns empty string if invalid.
 */
const formatSecondsToTime = (seconds) => {
  if (!seconds || typeof seconds !== "number" || seconds <= 0) return "";
  if (seconds % 3600 === 0) {
    // Exact hours
    const hours = seconds / 3600;
    return hours === 1 ? "1hour" : `${hours}hours`;
  } else if (seconds % 60 === 0) {
    // Exact minutes
    const minutes = seconds / 60;
    return minutes === 1 ? "1min" : `${minutes}mins`;
  } else {
    // Convert to minutes if not exact hours
    const minutes = Math.round(seconds / 60);
    return minutes === 1 ? "1min" : `${minutes}mins`;
  }
};

// Time options for dropdown
const TIME_OPTIONS = [
  { label: "5 minutes", value: "5mins" },
  { label: "10 minutes", value: "10mins" },
  { label: "15 minutes", value: "15mins" },
  { label: "30 minutes", value: "30mins" },
  { label: "45 minutes", value: "45mins" },
  { label: "1 hour", value: "1hour" },
  { label: "1.5 hours", value: "90mins" },
  { label: "2 hours", value: "2hours" },
  { label: "3 hours", value: "3hours" },
];

/**
 * Full-featured QuizModal
 *
 * Props:
 * - isOpen: boolean
 * - onClose: () => void
 * - selectedLesson: { lesson_id, lesson, module, program, ... }
 * - onSave: (payload) => void  // receives backend-ready payload
 */
export default function QuizModal({
  isOpen,
  onClose,
  selectedLesson,
  onSave,
  quizData = null,
  publisherFormData = null,
}) {
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(-1);
  const [quizSettings, setQuizSettings] = useState({
    totalPoints: 50,
    minimumPoints: 15,
    estimatedTime: "1hour",
    passingScore: 1,
    timeLimitSeconds: null,
    instructionText: "",
  });
  const [quizTitle, setQuizTitle] = useState("");
  const [selectedBatch, setSelectedBatch] = useState("");
  const [toast, setToast] = useState({ type: "", message: "" });
  const sidebarRef = useRef(null);
  const [openMenuIndex, setOpenMenuIndex] = useState(null);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const menuRef = useRef(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [questionToDelete, setQuestionToDelete] = useState(null);

  // Prefill when editing or when publisherFormData provided
  useEffect(() => {
    if (quizData) {
      // map quizData to local state
      setQuizTitle(quizData.title || quizData.quiz_title || "");
      setQuizSettings((prev) => ({
        ...prev,
        totalPoints: Math.max(
          1,
          Number(quizData.total_points) || prev.totalPoints,
        ),
        passingScore: Math.max(
          1,
          Number(quizData.passing_score) || prev.passingScore,
        ),
        timeLimitSeconds: quizData.time_limit_seconds ?? prev.timeLimitSeconds,
        estimatedTime:
          formatSecondsToTime(quizData.time_limit_seconds) ||
          prev.estimatedTime,
        instructionText: quizData.instruction_text ?? prev.instructionText,
      }));

      const mappedQuestions = (quizData.questions || []).map((q) => ({
        id: q.id ?? `${Date.now()}-${Math.random()}`,
        type:
          Object.keys(questionTypeMap).find(
            (k) => questionTypeMap[k] === (q.question_type || q.type),
          ) || "Multiple Choice",
        points: Math.max(1, q.points ?? q.points_value ?? 1),
        question: q.question_text || q.question || "",
        description: q.description || "",
        options: (q.options || []).map((o, idx) => ({
          id: `${q.id ?? Date.now()}-o${idx}`,
          text: o.option_text || o.text || "",
          isCorrect: o.is_correct ? true : false,
          position: o.position ?? idx + 1,
        })),
        matchingPairs: (q.matching_pairs || []).map((p, idx) => ({
          id: `${q.id}-m${idx}`,
          left: p.left,
          right: p.right,
        })),
        correctAnswer: q.correct_answer || q.correctAnswer || "",
        explanation: q.explanation || "",
        image_url: q.image_url || q.imageUrl || null,
      }));

      setQuestions(mappedQuestions);
      setCurrentQuestionIndex(mappedQuestions.length ? 0 : -1);
    } else {
      // new quiz: prefill batch from publisherFormData
      if (publisherFormData) {
        setSelectedBatch(publisherFormData.batch || "");
        if (publisherFormData.startDate) {
          setQuizSettings((p) => ({
            ...p,
            start_date: publisherFormData.startDate,
          }));
        }
        if (publisherFormData.endDate) {
          setQuizSettings((p) => ({
            ...p,
            end_date: publisherFormData.endDate,
          }));
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quizData, publisherFormData]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpenMenuIndex(null);
      }
    };

    if (openMenuIndex !== null) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openMenuIndex]);

  // map UI labels -> backend enum values
  const questionTypeMap = {
    "Multiple Choice": "MULTIPLE_CHOICE",
    "True/False": "TRUE_FALSE",
    "Single Choice": "SINGLE_CHOICE",
    "Short Answer": "SHORT_ANSWER",
    Matching: "MATCHING",
    "Upload Image": "UPLOAD_IMAGE",
    "Fill In the Blank": "FILL_IN_THE_BLANK",
  };

  // utility: get marks totals
  const calculateMarks = (list) => {
    const totalMarks = (list || questions).reduce(
      (sum, q) => sum + (Number(q.points) || 0),
      0,
    );
    const targetMarks = Number(quizSettings.totalPoints) || 0;
    const remaining = targetMarks - totalMarks;
    return { totalMarks, remaining };
  };
  const { totalMarks, remaining } = calculateMarks();

  // helper to ensure deep-clone question array
  const cloneQuestions = (src) => src.map((q) => JSON.parse(JSON.stringify(q)));

  // Validation helper function for adding new question
  const validateCurrentQuestionBeforeAdd = () => {
    // If no current question, allow adding
    if (currentQuestionIndex < 0 || !currentQuestion) {
      return { valid: true };
    }

    const q = currentQuestion;

    // 1. Question text must not be empty
    if (!String(q.question || "").trim()) {
      setToast({ type: "error", message: "Question text cannot be empty" });
      return { valid: false };
    }

    // 2. For "Multiple Choice", "Single Choice", "True/False"
    if (
      q.type === "Multiple Choice" ||
      q.type === "Single Choice" ||
      q.type === "True/False"
    ) {
      // At least 2 options
      if (!q.options || q.options.length < 2) {
        setToast({ type: "error", message: "At least 2 options are required" });
        return { valid: false };
      }
      // No empty option text
      const emptyOptions = q.options.filter(
        (o) => !String(o.text || "").trim(),
      );
      if (emptyOptions.length > 0) {
        setToast({ type: "error", message: "All options must have text" });
        return { valid: false };
      }
      // At least one correct option selected
      const hasCorrect = q.options.some((o) => o.isCorrect);
      if (!hasCorrect) {
        setToast({
          type: "error",
          message: "Select at least one correct option",
        });
        return { valid: false };
      }
    }

    // 3. For "Matching"
    if (q.type === "Matching") {
      // At least one pair
      if (!q.matchingPairs || q.matchingPairs.length < 1) {
        setToast({
          type: "error",
          message: "At least one matching pair is required",
        });
        return { valid: false };
      }
      // Both left and right values must be filled
      const emptyPairs = q.matchingPairs.filter(
        (p) => !String(p.left || "").trim() || !String(p.right || "").trim(),
      );
      if (emptyPairs.length > 0) {
        setToast({
          type: "error",
          message: "Both left and right values must be filled for all pairs",
        });
        return { valid: false };
      }
    }

    // 4. For "Short Answer" and "Fill In The Blank"
    if (q.type === "Short Answer" || q.type === "Fill In the Blank") {
      if (!String(q.correctAnswer || "").trim()) {
        setToast({ type: "error", message: "Correct answer cannot be empty" });
        return { valid: false };
      }
    }

    return { valid: true };
  };

  // add a new question and select it
  const addQuestion = (opts = {}) => {
    // Validate current question before adding new one
    const validation = validateCurrentQuestionBeforeAdd();
    if (!validation.valid) {
      return;
    }

    // Check if adding a new question will exceed total points
    const currentTotal = questions.reduce(
      (sum, q) => sum + (Number(q.points) || 0),
      0,
    );
    const maxPoints = Number(quizSettings.totalPoints);
    const defaultPoints = 2;

    if (currentTotal + defaultPoints > maxPoints) {
      setToast({
        type: "error",
        message: `Cannot add question. Total marks would exceed ${maxPoints}. Current total: ${currentTotal}`,
      });
      return;
    }

    const baseId = Date.now() + Math.floor(Math.random() * 1000);
    const newQ = {
      id: baseId,
      type: "Multiple Choice",
      points: defaultPoints,
      question: "",
      description: "",
      options: [
        { id: `${baseId}-o1`, text: "", isCorrect: false, position: 1 },
        { id: `${baseId}-o2`, text: "", isCorrect: false, position: 2 },
        { id: `${baseId}-o3`, text: "", isCorrect: false, position: 3 },
        { id: `${baseId}-o4`, text: "", isCorrect: false, position: 4 },
      ],
      matchingPairs: [],
      correctAnswer: "",
      explanation: "",
      uploadInstructions: "",
      imageFile: null,
      ...opts,
    };
    setQuestions((prev) => {
      const next = [...prev, newQ];
      // auto-select newly added question
      setTimeout(() => setCurrentQuestionIndex(next.length - 1), 0);
      return next;
    });
  };

  // add option to current question
  const addOption = () => {
    if (currentQuestionIndex < 0) return;
    setQuestions((prev) =>
      prev.map((q, i) =>
        i === currentQuestionIndex
          ? {
              ...q,
              options: [
                ...q.options,
                {
                  id: `${Date.now()}-${Math.random()}`,
                  text: "",
                  isCorrect: false,
                  position: q.options.length + 1,
                },
              ],
            }
          : q,
      ),
    );
  };

  // toggle option correctness (single vs multi)
  const toggleOption = (optionId) => {
    if (currentQuestionIndex < 0) return;
    setQuestions((prev) =>
      prev.map((q, idx) => {
        if (idx !== currentQuestionIndex) return q;
        const isSingle = q.type === "True/False" || q.type === "Single Choice";
        return {
          ...q,
          options: q.options.map((opt) =>
            isSingle
              ? { ...opt, isCorrect: opt.id === optionId }
              : opt.id === optionId
                ? { ...opt, isCorrect: !opt.isCorrect }
                : opt,
          ),
        };
      }),
    );
  };

  // remove option
  const removeOption = (optionId) => {
    if (currentQuestionIndex < 0) return;
    setQuestions((prev) =>
      prev.map((q, idx) =>
        idx !== currentQuestionIndex
          ? q
          : { ...q, options: q.options.filter((o) => o.id !== optionId) },
      ),
    );
  };

  // delete question with confirmation
  const deleteQuestion = (questionId, e) => {
    if (e) e.stopPropagation();
    setQuestionToDelete(questionId);
    setShowDeleteConfirm(true);
    setOpenMenuIndex(null);
  };

  const confirmDelete = () => {
    if (!questionToDelete) return;

    setQuestions((prev) => {
      const idx = prev.findIndex((p) => p.id === questionToDelete);
      const next = prev.filter((p) => p.id !== questionToDelete);
      // adjust current index
      if (next.length === 0) {
        setCurrentQuestionIndex(-1);
      } else if (idx === currentQuestionIndex) {
        setCurrentQuestionIndex(Math.max(0, idx - 1));
      } else if (idx < currentQuestionIndex) {
        setCurrentQuestionIndex((i) => Math.max(0, i - 1));
      }
      return next;
    });

    setShowDeleteConfirm(false);
    setQuestionToDelete(null);
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setQuestionToDelete(null);
  };

  // update a question field immutably
  const updateQuestion = (field, value) => {
    if (currentQuestionIndex < 0) return;
    setQuestions((prev) =>
      prev.map((q, idx) =>
        idx === currentQuestionIndex ? { ...q, [field]: value } : q,
      ),
    );
  };

  // update option text
  const updateOptionText = (optionIndex, text) => {
    if (currentQuestionIndex < 0) return;
    setQuestions((prev) =>
      prev.map((q, idx) =>
        idx !== currentQuestionIndex
          ? q
          : {
              ...q,
              options: q.options.map((opt, i) =>
                i === optionIndex ? { ...opt, text } : opt,
              ),
            },
      ),
    );
  };

  // change question type and normalize fields
  const handleTypeChange = (newType) => {
    if (currentQuestionIndex < 0) return;
    setQuestions((prev) =>
      prev.map((q, idx) => {
        if (idx !== currentQuestionIndex) return q;
        const copy = { ...q, type: newType };
        if (newType === "True/False") {
          copy.options = [
            { id: `${q.id}-T`, text: "True", isCorrect: false, position: 1 },
            { id: `${q.id}-F`, text: "False", isCorrect: false, position: 2 },
          ];
        } else if (
          newType === "Single Choice" ||
          newType === "Multiple Choice"
        ) {
          if (!q.options || q.options.length < 2) {
            copy.options = [
              { id: `${q.id}-o1`, text: "", isCorrect: false, position: 1 },
              { id: `${q.id}-o2`, text: "", isCorrect: false, position: 2 },
              { id: `${q.id}-o3`, text: "", isCorrect: false, position: 3 },
              { id: `${q.id}-o4`, text: "", isCorrect: false, position: 4 },
            ];
          }
        } else if (
          newType === "Short Answer" ||
          newType === "Fill In the Blank" ||
          newType === "Upload Image"
        ) {
          copy.options = [];
          copy.correctAnswer = "";
        } else if (newType === "Matching") {
          copy.options = [];
          copy.matchingPairs =
            copy.matchingPairs && copy.matchingPairs.length > 0
              ? copy.matchingPairs
              : [
                  { id: `${q.id}-m1`, left: "", right: "" },
                  { id: `${q.id}-m2`, left: "", right: "" },
                ];
        }
        return copy;
      }),
    );
  };

  // matching pair helpers
  const addMatchingPair = () => {
    if (currentQuestionIndex < 0) return;
    setQuestions((prev) =>
      prev.map((q, idx) =>
        idx !== currentQuestionIndex
          ? q
          : {
              ...q,
              matchingPairs: [
                ...(q.matchingPairs || []),
                { id: `${q.id}-m${Date.now()}`, left: "", right: "" },
              ],
            },
      ),
    );
  };

  const updateMatchingPair = (pairIndex, side, value) => {
    if (currentQuestionIndex < 0) return;
    setQuestions((prev) =>
      prev.map((q, idx) =>
        idx !== currentQuestionIndex
          ? q
          : {
              ...q,
              matchingPairs: q.matchingPairs.map((p, i) =>
                i === pairIndex ? { ...p, [side]: value } : p,
              ),
            },
      ),
    );
  };

  const removeMatchingPair = (pairId) => {
    if (currentQuestionIndex < 0) return;
    setQuestions((prev) =>
      prev.map((q, idx) =>
        idx !== currentQuestionIndex
          ? q
          : {
              ...q,
              matchingPairs: q.matchingPairs.filter((p) => p.id !== pairId),
            },
      ),
    );
  };

  // file upload for Upload Image question
  const handleImageFile = (file) => {
    if (currentQuestionIndex < 0) return;
    setQuestions((prev) =>
      prev.map((q, idx) =>
        idx === currentQuestionIndex ? { ...q, imageFile: file } : q,
      ),
    );
  };

  // reorder questions: move up / down
  const moveQuestion = (index, direction) => {
    setQuestions((prev) => {
      const next = [...prev];
      const newIndex = index + direction;
      if (newIndex < 0 || newIndex >= next.length) return prev;
      const temp = next[newIndex];
      next[newIndex] = next[index];
      next[index] = temp;
      // if currently selected question moved, update index
      if (currentQuestionIndex === index) {
        setCurrentQuestionIndex(newIndex);
      } else if (currentQuestionIndex === newIndex) {
        setCurrentQuestionIndex(index);
      }
      return next;
    });
  };

  // quick swap between two indexes (used by swap icon)
  const swapQuestions = (i, j) => {
    if (i < 0 || j < 0 || i >= questions.length || j >= questions.length)
      return;
    setQuestions((prev) => {
      const next = [...prev];
      const tmp = next[i];
      next[i] = next[j];
      next[j] = tmp;
      if (currentQuestionIndex === i) setCurrentQuestionIndex(j);
      else if (currentQuestionIndex === j) setCurrentQuestionIndex(i);
      return next;
    });
  };

  // Drag and Drop handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/html", e.currentTarget);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    // Reorder questions
    setQuestions((prev) => {
      const newQuestions = [...prev];
      const draggedQuestion = newQuestions[draggedIndex];
      newQuestions.splice(draggedIndex, 1);
      newQuestions.splice(index, 0, draggedQuestion);

      // Update current question index
      if (currentQuestionIndex === draggedIndex) {
        setCurrentQuestionIndex(index);
      } else if (
        draggedIndex < currentQuestionIndex &&
        index >= currentQuestionIndex
      ) {
        setCurrentQuestionIndex(currentQuestionIndex - 1);
      } else if (
        draggedIndex > currentQuestionIndex &&
        index <= currentQuestionIndex
      ) {
        setCurrentQuestionIndex(currentQuestionIndex + 1);
      }

      setDraggedIndex(index);
      return newQuestions;
    });
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  // validations & payload builder
  const validateAndBuildPayload = () => {
    // basic validations
    if (!quizTitle.trim()) throw new Error("Please enter quiz title");
    const lessonId = selectedLesson?.lesson_id ?? selectedLesson?.lessonDbId;
    if (!lessonId) throw new Error("Lesson is required");
    if (!questions.length) throw new Error("Please add at least one question");

    // Validate quiz points minimum
    if (Number(quizSettings.totalPoints) < 1) {
      throw new Error("Quiz points must be at least 1");
    }

    // Validate passing score minimum
    if (Number(quizSettings.passingScore) < 1) {
      throw new Error("Passing score must be at least 1");
    }

    // Validate passing score doesn't exceed total points
    if (Number(quizSettings.passingScore) > Number(quizSettings.totalPoints)) {
      throw new Error("Passing score cannot exceed total quiz points");
    }

    // Validate estimated time
    const parsedSeconds = parseTimeToSeconds(quizSettings.estimatedTime);
    if (quizSettings.estimatedTime && !parsedSeconds) {
      throw new Error(
        "Invalid time format. Use formats like '30min', '45mins', '1hour', '2hours'.",
      );
    }

    const { totalMarks } = calculateMarks(questions);
    if (totalMarks !== Number(quizSettings.totalPoints)) {
      throw new Error(
        `Total question points must equal quiz total points (${quizSettings.totalPoints}). Currently ${totalMarks}`,
      );
    }

    // validate each question
    for (let i = 0; i < questions.length; i++) {
      const q = questions[i];
      if (!String(q.question || "").trim())
        throw new Error(`Question ${i + 1} is empty`);
      if (Number(q.points) < 1)
        throw new Error(`Question ${i + 1}: Points must be at least 1`);
      // for types with options, ensure at least one correct
      const noAnswerTypes = ["Fill In the Blank"]; // old naming fallback; we'll use type comparisons where needed
      if (
        q.type === "Multiple Choice" ||
        q.type === "Single Choice" ||
        q.type === "True/False"
      ) {
        if (!q.options || !q.options.length)
          throw new Error(`Question ${i + 1} has no options`);
        if (!q.options.some((o) => o.isCorrect))
          throw new Error(
            `Mark at least one correct option for Question ${i + 1}`,
          );
      }
      // matching: ensure pairs present
      if (q.type === "Matching") {
        if (!q.matchingPairs || q.matchingPairs.length < 1)
          throw new Error(`Question ${i + 1} matching needs at least one pair`);
      }
    }

    // Build payload according to backend QuizCreateSchema:
    // Quiz: lesson_id, title, total_points, passing_score, time_limit_seconds, instruction_text, questions: []
    const quizPayload = {
      lesson_id: Number(lessonId),
      title: quizTitle,
      total_points: Number(quizSettings.totalPoints),
      passing_score: Number(
        quizSettings.passingScore || quizSettings.minimumPoints || 1,
      ),
      time_limit_seconds: parsedSeconds || null,
      instruction_text: quizSettings.instructionText || null,
      questions: questions.map((q, idx) => {
        // map options
        const options =
          q.options && q.options.length
            ? q.options.map((o, pos) => ({
                position: pos + 1,
                option_text: o.text || "",
                is_correct: o.isCorrect ? 1 : 0,
              }))
            : [];

        // matching pairs mapping - only include pairs with non-empty left and right text
        const matching_pairs =
          q.matchingPairs && q.matchingPairs.length
            ? q.matchingPairs
                .filter((p) => p.left && p.right) // Only include pairs with both sides filled
                .map((p, pos) => ({
                  position: pos + 1,
                  left_text: p.left,
                  right_text: p.right,
                }))
            : [];

        return {
          position: idx + 1,
          question_text: q.question,
          question_type:
            questionTypeMap[q.type] || questionTypeMap["Multiple Choice"],
          points: Number(q.points || 1),
          correct_answer: q.correctAnswer || null,
          explanation: q.explanation || null,
          options,
          matching_pairs,
          // If there's an image file, include it for uploader to handle separately
          imageFile: q.imageFile || null,
        };
      }),
    };

    return quizPayload;
  };

  // submit handler
  const handleSubmit = async () => {
    try {
      let payload = validateAndBuildPayload();

      // include publisherFormData if present
      if (publisherFormData) {
        payload = {
          ...payload,
          start_date: publisherFormData.startDate || null,
          end_date: publisherFormData.endDate || null,
          batch: publisherFormData.batch || payload.batch || null,
        };
      }

      // Upload question images (if any) and replace imageFile with image_url
      for (let i = 0; i < payload.questions.length; i++) {
        const q = payload.questions[i];
        if (q.imageFile) {
          try {
            const url = await uploadImageToCloudinary(q.imageFile);
            q.image_url = url;
            delete q.imageFile;
          } catch (err) {
            console.error("Image upload failed for question", i, err);
            // continue without image
            q.image_url = null;
            delete q.imageFile;
          }
        }
      }

      const isEdit = quizData && (quizData.quiz_id || quizData.id);
      const url = isEdit
        ? `${API_URL}/quiz/${quizData.quiz_id ?? quizData.id}/edit`
        : `${API_URL}/quiz/create`;

      const method = isEdit ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (res.ok || data.success) {
        setToast({
          type: "success",
          message:
            data.message ||
            `Quiz ${isEdit ? "updated" : "created"} successfully`,
        });
        // Delay notifying parent and closing so toast can be seen while modal is mounted
        setTimeout(() => {
          if (onSave) {
            try {
              onSave(data.data || data);
            } catch (err) {
              console.error("onSave callback error:", err);
            }
          } else {
            onClose();
          }
        }, 2500);
      } else {
        console.error("Quiz save failed:", data);
        let errorMessage = "Failed to save quiz";

        if (data.message) {
          errorMessage = data.message;
        } else if (data.detail) {
          // Handle Pydantic validation errors (array of error objects)
          if (Array.isArray(data.detail)) {
            errorMessage = data.detail
              .map((err) => err.msg || JSON.stringify(err))
              .join(", ");
          } else if (typeof data.detail === "string") {
            errorMessage = data.detail;
          } else if (typeof data.detail === "object") {
            // Handle other object formats
            errorMessage = JSON.stringify(data.detail);
          }
        }

        setToast({
          type: "error",
          message: errorMessage,
        });
      }
    } catch (err) {
      console.error(err);
      let errorMessage = "Validation failed";

      if (err.message) {
        errorMessage = err.message;
      } else if (typeof err === "object") {
        errorMessage = JSON.stringify(err);
      }

      setToast({
        type: "error",
        message: errorMessage,
      });
    }
  };

  // effect: scroll selected item into view
  useEffect(() => {
    if (!sidebarRef.current) return;
    const el = sidebarRef.current.querySelector(
      `[data-idx="${currentQuestionIndex}"]`,
    );
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [currentQuestionIndex, questions.length]);

  if (!isOpen) return null;

  const currentQuestion =
    currentQuestionIndex >= 0 ? questions[currentQuestionIndex] : null;
  const progressPercentage =
    quizSettings.totalPoints > 0
      ? (totalMarks / Number(quizSettings.totalPoints)) * 100
      : 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="bg-white w-full max-w-7xl rounded-lg shadow-lg overflow-y-auto max-h-[95vh]">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-gray-900">Add Quiz</h1>
            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="w-5 h-5 text-gray-700" />
              </button>
            </div>
          </div>

          {/* Program Info */}
          <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg shadow-md border border-gray-200 p-6 mb-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <p className="text-sm text-gray-600 font-medium">Program</p>
                <p className="text-lg font-semibold text-zinc-800 mt-1">
                  {selectedLesson?.courseTitle}
                </p>

                <p className="text-sm text-gray-600 font-medium mt-6">
                  Module {selectedLesson?.moduleDisplayId ?? "-"}
                </p>
                <p className="text-lg font-semibold text-zinc-800 mt-1">
                  {selectedLesson?.moduleTitle || selectedLesson?.module || "—"}
                </p>
              </div>

              <div>
                <p className="text-sm text-gray-600 font-medium">
                  Lesson {selectedLesson?.lessonDisplayId ?? "-"}
                </p>
                <p className="text-lg font-semibold text-zinc-800 mt-1">
                  {selectedLesson?.lessonTitle || selectedLesson?.lesson || "—"}
                </p>
              </div>
            </div>
          </div>

          {/* Header row: sidebar + settings */}
          <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6 mb-6">
            <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div className="text-sm font-normal text-gray-900">
                  Questions ({questions.length})
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      if (
                        typeof currentQuestionIndex === "number" &&
                        currentQuestionIndex >= 0 &&
                        currentQuestionIndex < questions.length - 1
                      ) {
                        swapQuestions(
                          currentQuestionIndex,
                          currentQuestionIndex + 1,
                        );
                      }
                    }}
                    className="w-8 h-8 flex items-center justify-center bg-white border border-gray-300 rounded hover:bg-gray-100"
                    title="Swap with next question"
                  >
                    <img src={Qus_swap} className="w-4 h-4" alt="swap" />
                  </button>

                  <button
                    onClick={() => addQuestion()}
                    className="w-8 h-8 flex items-center justify-center bg-[#4AACC5] text-white rounded hover:bg-[#3d9bb0]"
                    title="Add Question"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Progress */}
              <div className="mt-3">
                <div className="flex justify-between text-xs text-gray-600 mb-1">
                  <span>{totalMarks} Mark</span>
                  <span>Remaining {remaining >= 0 ? remaining : 0}</span>
                </div>
                <div className="w-full h-1 bg-gray-200 rounded overflow-hidden">
                  <div
                    className="h-full bg-[#4AACC5] rounded transition-all duration-300"
                    style={{ width: `${Math.min(progressPercentage, 100)}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Quiz Settings */}
            <div className="bg-neutral-100 rounded shadow-sm border border-gray-200 p-6">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-7">
                <div>
                  <label className="text-sm font-bold text-gray-800 mb-2 flex items-center gap-1">
                    Quiz Points <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="number"
                    value={quizSettings.totalPoints}
                    onChange={(e) => {
                      const value = e.target.value;
                      const numValue = parseInt(value, 10);

                      if (isNaN(numValue) || numValue < 1) {
                        setQuizSettings({
                          ...quizSettings,
                          totalPoints: 1,
                        });
                      } else {
                        setQuizSettings({
                          ...quizSettings,
                          totalPoints: numValue,
                        });
                      }
                    }}
                    onBlur={(e) => {
                      const value = e.target.value;
                      const numValue = parseInt(value, 10);
                      if (isNaN(numValue) || numValue < 1) {
                        setQuizSettings({
                          ...quizSettings,
                          totalPoints: 1,
                        });
                      }
                    }}
                    className="w-full max-w-[160px] px-3 py-2 bg-white border border-zinc-200 rounded shadow-sm text-sm text-zinc-600"
                    min="1"
                  />
                </div>
                <div>
                  <label className="text-sm font-bold text-gray-800 mb-2 flex items-center gap-1">
                    Passing Score <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="number"
                    value={quizSettings.passingScore}
                    onChange={(e) => {
                      const value = e.target.value;
                      const numValue = parseInt(value, 10);

                      if (isNaN(numValue) || numValue < 1) {
                        setQuizSettings({
                          ...quizSettings,
                          passingScore: 1,
                        });
                      } else if (numValue > quizSettings.totalPoints) {
                        setToast({
                          type: "error",
                          message:
                            "Passing score cannot exceed total quiz points",
                        });
                        setQuizSettings({
                          ...quizSettings,
                          passingScore: quizSettings.totalPoints,
                        });
                      } else {
                        setQuizSettings({
                          ...quizSettings,
                          passingScore: numValue,
                        });
                      }
                    }}
                    className="w-full max-w-[160px] px-3 py-2 bg-white border border-zinc-200 rounded shadow-sm text-sm text-zinc-600"
                    min="1"
                    max={quizSettings.totalPoints}
                  />
                </div>
                <div>
                  <label className="text-sm font-bold text-gray-800 mb-2 flex items-center gap-1">
                    Overall Estimate Time{" "}
                    <span className="text-red-600">*</span>
                  </label>
                  <div className="relative w-full max-w-[160px]">
                    <select
                      value={quizSettings.estimatedTime}
                      onChange={(e) =>
                        setQuizSettings({
                          ...quizSettings,
                          estimatedTime: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 bg-white border border-zinc-200 rounded shadow-sm text-sm text-zinc-600 appearance-none"
                    >
                      {TIME_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-2.5 w-4 h-4 text-zinc-400 pointer-events-none" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main grid: sidebar (list) + editor */}
          <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
            {/* Sidebar list */}
            <div
              className="bg-white rounded shadow-sm border border-gray-200 p-4 max-h-[600px] overflow-y-auto"
              ref={sidebarRef}
            >
              <div className="space-y-2">
                {questions.map((q, idx) => (
                  <div
                    key={q.id}
                    data-idx={idx}
                    draggable
                    onDragStart={(e) => handleDragStart(e, idx)}
                    onDragOver={(e) => handleDragOver(e, idx)}
                    onDragEnd={handleDragEnd}
                    onClick={() => setCurrentQuestionIndex(idx)}
                    className={`relative p-3 rounded-lg border cursor-move transition-all ${
                      idx === currentQuestionIndex
                        ? "bg-cyan-50 border-[#4AACC5] shadow-md"
                        : "border-gray-200 hover:bg-gray-50"
                    } ${draggedIndex === idx ? "opacity-50" : "opacity-100"}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-start gap-3 flex-1">
                        <div className="w-6 h-6 bg-white border border-gray-300 rounded flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-semibold text-gray-700">
                            {idx + 1}
                          </span>
                        </div>
                        <p className="text-sm font-medium text-gray-900 line-clamp-2 flex-1 break-words">
                          {q.question || "Untitled question"}
                        </p>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenMenuIndex(openMenuIndex === idx ? null : idx);
                        }}
                        className="text-gray-400 hover:text-gray-600 flex-shrink-0 p-1"
                        title="Options"
                      >
                        <svg
                          className="w-4 h-4"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <circle cx="12" cy="5" r="2" />
                          <circle cx="12" cy="12" r="2" />
                          <circle cx="12" cy="19" r="2" />
                        </svg>
                      </button>
                    </div>

                    <div className="flex items-center justify-between ml-9">
                      <span className="text-xs text-gray-500 font-medium">
                        {q.type}
                      </span>
                      <p className="text-xs text-[#4AACC5] font-semibold">
                        {q.points} pts
                      </p>
                    </div>

                    {/* Delete Menu Dropdown */}
                    {openMenuIndex === idx && (
                      <div
                        ref={menuRef}
                        onClick={(e) => e.stopPropagation()}
                        className="absolute right-3 top-12 w-36 bg-white border border-gray-200 rounded shadow-lg z-50"
                      >
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteQuestion(q.id, e);
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded"
                        >
                          Delete Question
                        </button>
                      </div>
                    )}
                  </div>
                ))}
                {questions.length === 0 && (
                  <div className="text-center py-8 text-gray-400 text-sm">
                    No questions yet. Click + to add.
                  </div>
                )}
              </div>
            </div>

            {/* Editor */}
            {currentQuestion ? (
              <div className="bg-white rounded shadow-sm border border-gray-200 p-6 max-h-[600px] overflow-y-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-7 mb-6">
                  <div>
                    <label className="text-sm font-bold text-gray-800 mb-2 flex items-center gap-1">
                      Question Type <span className="text-red-600">*</span>
                    </label>
                    <div className="relative">
                      <select
                        value={currentQuestion.type}
                        onChange={(e) => handleTypeChange(e.target.value)}
                        className="w-full px-3 py-2 bg-white border border-zinc-200 rounded shadow-sm text-sm text-zinc-600 appearance-none"
                      >
                        <option>Multiple Choice</option>
                        <option>True/False</option>
                        <option>Single Choice</option>
                        <option>Short Answer</option>
                        <option>Matching</option>
                        <option>Upload Image</option>
                        <option>Fill In the Blank</option>
                      </select>
                      <ChevronDown className="absolute right-3 top-3 w-4 h-4 text-zinc-400 pointer-events-none" />
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-bold text-gray-800 mb-2 flex items-center gap-1">
                      Points <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="number"
                      value={currentQuestion.points}
                      onChange={(e) => {
                        const inputValue = e.target.value;
                        const numValue = parseInt(inputValue, 10);

                        if (isNaN(numValue) || numValue < 1) {
                          updateQuestion("points", 1);
                        } else {
                          // Check if new total will exceed
                          const otherQuestionsTotal = questions
                            .filter((_, idx) => idx !== currentQuestionIndex)
                            .reduce(
                              (sum, q) => sum + (Number(q.points) || 0),
                              0,
                            );

                          const newTotal = otherQuestionsTotal + numValue;
                          const maxPoints = Number(quizSettings.totalPoints);

                          if (newTotal > maxPoints) {
                            // Don't allow - show warning
                            setToast({
                              type: "error",
                              message: `Total marks cannot exceed ${maxPoints}.`,
                            });
                            // Keep previous value or set to max allowed
                            const maxAllowed = maxPoints - otherQuestionsTotal;
                            updateQuestion("points", Math.max(1, maxAllowed));
                          } else {
                            updateQuestion("points", numValue);
                          }
                        }
                      }}
                      onBlur={(e) => {
                        const inputValue = e.target.value;
                        const numValue = parseInt(inputValue, 10);
                        if (isNaN(numValue) || numValue < 1) {
                          updateQuestion("points", 1);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "-" || e.key === "e" || e.key === "E") {
                          e.preventDefault();
                        }
                      }}
                      className="w-full px-3 py-2 bg-white border border-zinc-200 rounded shadow-sm text-sm text-zinc-600"
                      min="1"
                      step="1"
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <p className="text-base font-medium text-gray-900 mb-3">
                    Question : {currentQuestionIndex + 1} (
                    {currentQuestion.type})
                  </p>
                  <input
                    type="text"
                    value={currentQuestion.question}
                    onChange={(e) => updateQuestion("question", e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-zinc-200 rounded shadow-sm text-sm text-neutral-700"
                    placeholder="Enter question text"
                  />
                </div>

                <div className="mb-6">
                  <label className="text-sm font-bold text-gray-800 mb-2 block">
                    Description (Optional)
                  </label>
                  <textarea
                    value={currentQuestion.description}
                    onChange={(e) =>
                      updateQuestion("description", e.target.value)
                    }
                    className="w-full px-3 py-2 bg-white border border-zinc-200 rounded shadow-sm text-sm text-zinc-600"
                    placeholder="Write description..."
                    rows="3"
                  />
                </div>

                {/* Options */}
                {(currentQuestion.type === "Multiple Choice" ||
                  currentQuestion.type === "Single Choice" ||
                  currentQuestion.type === "True/False") && (
                  <div className="mb-6">
                    {(currentQuestion.type === "Multiple Choice" ||
                      currentQuestion.type === "Single Choice") && (
                      <button
                        onClick={addOption}
                        className="flex items-center gap-2 px-2.5 py-2.5 bg-neutral-100 border border-stone-300 rounded shadow-sm mb-4 hover:bg-gray-200"
                      >
                        <Plus className="w-2.5 h-2.5" />
                        <span className="text-sm font-medium text-gray-900">
                          Add option
                        </span>
                      </button>
                    )}
                    <div className="space-y-3.5">
                      {currentQuestion.options?.map((opt, idx) => (
                        <div key={opt.id} className="flex items-center gap-3">
                          <button
                            onClick={() => toggleOption(opt.id)}
                            className={`w-6 h-6 rounded border shadow-sm flex items-center justify-center flex-shrink-0 transition-colors ${
                              opt.isCorrect
                                ? "bg-green-500 border-green-500"
                                : "bg-gray-200 border-gray-300"
                            }`}
                            title="Mark correct"
                          >
                            {opt.isCorrect && (
                              <svg
                                className="w-3 h-2 text-white"
                                fill="none"
                                viewBox="0 0 12 8"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={1.5}
                                  d="M1 4l3 3 7-7"
                                />
                              </svg>
                            )}
                          </button>
                          <input
                            type="text"
                            value={opt.text}
                            onChange={(e) =>
                              updateOptionText(idx, e.target.value)
                            }
                            placeholder={
                              currentQuestion.type === "True/False"
                                ? opt.text
                                : `${String.fromCharCode(
                                    65 + idx,
                                  )}. Option text`
                            }
                            disabled={currentQuestion.type === "True/False"}
                            className={`flex-1 px-3 py-2 border rounded shadow-sm text-sm text-neutral-700 ${
                              opt.isCorrect
                                ? "bg-emerald-50 border-gray-300"
                                : "bg-white border-zinc-200"
                            } ${
                              currentQuestion.type === "True/False"
                                ? "cursor-not-allowed"
                                : ""
                            }`}
                          />
                          {(currentQuestion.type === "Multiple Choice" ||
                            currentQuestion.type === "Single Choice") &&
                            currentQuestion.options.length > 2 && (
                              <button
                                onClick={() => removeOption(opt.id)}
                                className="text-gray-400 hover:text-red-500 flex-shrink-0"
                                title="Remove option"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Short Answer */}
                {currentQuestion.type === "Short Answer" && (
                  <div className="mb-6">
                    <label className="text-sm font-bold text-gray-800 mb-2 block">
                      Correct Answer
                    </label>
                    <input
                      type="text"
                      value={currentQuestion.correctAnswer || ""}
                      onChange={(e) =>
                        updateQuestion("correctAnswer", e.target.value)
                      }
                      className="w-full px-3 py-2 bg-white border border-zinc-200 rounded shadow-sm text-sm text-zinc-600"
                      placeholder="Enter the correct answer"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Students will type their answer in a text box
                    </p>
                  </div>
                )}

                {/* Fill in the Blank */}
                {currentQuestion.type === "Fill In the Blank" && (
                  <div className="mb-6">
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded mb-3">
                      <p className="text-sm text-blue-800">
                        <strong>Tip:</strong> Use underscores (___) in your
                        question to indicate blank spaces. Example: "The capital
                        of France is ___."
                      </p>
                    </div>
                    <label className="text-sm font-bold text-gray-800 mb-2 block">
                      Correct Answer
                    </label>
                    <input
                      type="text"
                      value={currentQuestion.correctAnswer || ""}
                      onChange={(e) =>
                        updateQuestion("correctAnswer", e.target.value)
                      }
                      className="w-full px-3 py-2 bg-white border border-zinc-200 rounded shadow-sm text-sm text-zinc-600"
                      placeholder="Enter the correct answer for the blank"
                    />
                  </div>
                )}

                {/* Matching */}
                {currentQuestion.type === "Matching" && (
                  <div className="mb-6">
                    <button
                      onClick={addMatchingPair}
                      className="flex items-center gap-2 px-2.5 py-2.5 bg-neutral-100 border border-stone-300 rounded shadow-sm mb-4 hover:bg-gray-200"
                    >
                      <Plus className="w-2.5 h-2.5" />
                      <span className="text-sm font-medium text-gray-900">
                        Add matching pair
                      </span>
                    </button>
                    <div className="space-y-3">
                      {currentQuestion.matchingPairs?.map((pair, idx) => (
                        <div
                          key={pair.id}
                          className="grid grid-cols-[1fr_auto_1fr_auto] gap-3 items-center"
                        >
                          <input
                            type="text"
                            value={pair.left}
                            onChange={(e) =>
                              updateMatchingPair(idx, "left", e.target.value)
                            }
                            className="px-3 py-2 bg-white border border-zinc-200 rounded shadow-sm text-sm text-neutral-700"
                            placeholder="Left item"
                          />
                          <span className="text-gray-400">↔</span>
                          <input
                            type="text"
                            value={pair.right}
                            onChange={(e) =>
                              updateMatchingPair(idx, "right", e.target.value)
                            }
                            className="px-3 py-2 bg-white border border-zinc-200 rounded shadow-sm text-sm text-neutral-700"
                            placeholder="Right item"
                          />
                          {currentQuestion.matchingPairs.length > 1 && (
                            <button
                              onClick={() => removeMatchingPair(pair.id)}
                              className="text-gray-400 hover:text-red-500"
                              title="Remove pair"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Upload Image */}
                {currentQuestion.type === "Upload Image" && (
                  <div className="mb-6">
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded mb-3">
                      <p className="text-sm text-blue-800">
                        <strong>Note:</strong> Students will upload an image as
                        their answer to this question.
                      </p>
                    </div>
                    <label className="text-sm font-bold text-gray-800 mb-2 block">
                      Instructions for Students
                    </label>
                    <textarea
                      value={currentQuestion.uploadInstructions || ""}
                      onChange={(e) =>
                        updateQuestion("uploadInstructions", e.target.value)
                      }
                      className="w-full px-3 py-2 bg-white border border-zinc-200 rounded shadow-sm text-sm text-zinc-600"
                      placeholder="E.g., Upload a labeled diagram of a cell"
                      rows="2"
                    />
                    <div className="mt-3">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) =>
                          handleImageFile(
                            e.target.files ? e.target.files[0] : null,
                          )
                        }
                      />
                      {currentQuestion.imageFile && (
                        <p className="text-xs text-green-600 mt-2">
                          File selected: {currentQuestion.imageFile.name}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Explanation */}
                <div className="mb-6">
                  <label className="text-sm font-bold text-gray-800 mb-2 block">
                    Answer Explanation
                  </label>
                  <textarea
                    value={currentQuestion.explanation || ""}
                    onChange={(e) =>
                      updateQuestion("explanation", e.target.value)
                    }
                    className="w-full px-3 py-2 bg-white border border-zinc-200 rounded shadow-sm text-sm text-zinc-600"
                    placeholder="Write explanation..."
                    rows="3"
                  />
                </div>

                <div className="flex justify-end">
                  <button
                    onClick={() => addQuestion()}
                    className="bg-[#4AACC5] text-white px-4 py-2 rounded shadow hover:bg-[#74b8c9] text-sm font-medium"
                  >
                    + Add New Question
                  </button>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded shadow-sm border border-gray-200 p-6 flex items-center justify-center min-h-[400px]">
                <div className="text-center text-gray-500">
                  <p className="mb-4">No question selected</p>
                  <button
                    onClick={() => addQuestion()}
                    className="px-4 py-2 bg-[#4AACC5] text-white rounded hover:bg-teal-500"
                  >
                    Add Your First Question
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="border-t border-gray-200 my-6" />

          {/* Quiz Title & optional instruction */}
          <div className="flex flex-col items-start mb-6">
            <label className="text-base font-medium text-gray-900 mb-3 block">
              Enter Quiz Title <span className="text-red-600">*</span>
            </label>
            <input
              type="text"
              value={quizTitle}
              onChange={(e) => setQuizTitle(e.target.value)}
              placeholder="Quiz title"
              className="w-full max-w-2xl px-3 py-2 bg-white border border-zinc-200 rounded shadow-sm text-sm text-neutral-700"
            />
            <label className="text-sm mt-3 text-gray-700">
              Instruction (optional)
            </label>
            <textarea
              value={quizSettings.instructionText}
              onChange={(e) =>
                setQuizSettings({
                  ...quizSettings,
                  instructionText: e.target.value,
                })
              }
              className="w-full max-w-2xl px-3 py-2 bg-white border border-zinc-200 rounded shadow-sm text-sm text-neutral-700 mt-2"
              rows="2"
              placeholder="Instructions for students (optional)"
            />
          </div>

          {/* Footer */}
          <div className="flex justify-between items-center gap-3">
            <button
              onClick={onClose}
              className="flex-1 h-11 px-6 py-3 rounded-[3px] outline outline-2 outline-offset-[-2px] outline-[#4AACC5] text-[#4AACC5] text-base font-bold hover:bg-teal-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              className="flex-1 h-11 px-6 pt-3.5 pb-4 bg-[#4AACC5] rounded-[3px] text-white text-base font-bold hover:bg-[#88cfe0] transition-colors"
            >
              Save Quiz
            </button>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-bold text-gray-900 mb-3">
              Delete Question?
            </h3>
            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to delete this question? This action cannot
              be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={cancelDelete}
                className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 font-medium"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      <ToastMessage
        type={toast.type}
        message={toast.message}
        onClose={() => setToast({ type: "", message: "" })}
      />
    </div>
  );
}
